/**
 * "Existing source" tab.
 *
 * Loads the top-ranked existing assets and validates them against the gap
 * via the batched validate-assets endpoint, then lets the user apply a source
 * once the advisory verdict is available. Two `useQuery`s — one for the list
 * and one for the batch — own the cache, so tab switches and modal close /
 * reopen on the same gap render instantly from cache (within React Query's
 * 5-min staleTime / gcTime).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  FileText,
  Loader2,
  MinusCircle,
  RotateCcw,
  Video,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import knowledgeGapsService, {
  type KGAssetErrorCode,
  type KGAssetType,
  type KGAssetValidationResponse,
  type KGRankedAsset,
  type KGValidateAssetsBatchResultRow,
  type KGValidationVerdict,
} from '@/lib/api/knowledgeGaps';

const SUGGESTED_LIMIT = 10;

const ASSET_ICON: Record<KGAssetType, LucideIcon> = {
  video: Video,
  presentation_deck: FileText,
};

const ASSET_TYPE_LABEL: Record<KGAssetType, string> = {
  video: 'Video',
  presentation_deck: 'Document',
};
const GENERIC_SOURCE_LOAD_ERROR = 'We could not load sources right now. Try again.';
const GENERIC_SOURCE_VALIDATE_ERROR = 'We could not check these sources right now. Try again.';

const VERDICT_META: Record<
  KGValidationVerdict,
  { label: string; className: string; Icon: LucideIcon }
> = {
  addresses: {
    label: 'Addresses the gap',
    className:
      'border-emerald-500/40 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300',
    Icon: CheckCircle2,
  },
  partial: {
    label: 'Partially addresses the gap',
    className:
      'border-amber-500/40 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300',
    Icon: AlertTriangle,
  },
  not_relevant: {
    label: 'Does not address the gap',
    className:
      'border-rose-500/40 bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300',
    Icon: XCircle,
  },
};

type ValidatedRow = {
  asset: KGRankedAsset;
  validation: KGAssetValidationResponse;
};

type FailedRow = {
  asset: KGRankedAsset;
  error: { code: KGAssetErrorCode; message: string };
};

class KGRequestError extends Error {
  code?: KGAssetErrorCode;
  constructor(message: string, code?: KGAssetErrorCode) {
    super(message);
    this.name = 'KGRequestError';
    this.code = code;
  }
}

export function ExistingSourceTab({
  orgId,
  summaryId,
  allowedTypes,
  onApply,
  isApplying,
  selectedAssetKey,
}: {
  orgId: string;
  summaryId: string;
  allowedTypes: ReadonlyArray<KGAssetType>;
  onApply: (asset: KGRankedAsset) => void;
  isApplying: boolean;
  selectedAssetKey: string | null;
}) {
  // Shared cache key prefix for both the list and the batch. Including
  // allowedTypes in the list key prevents a category-scoped re-render from
  // returning a stale list cached under a different filter.
  const allowedKey = allowedTypes.join('|');

  const listQuery = useQuery({
    queryKey: ['kg-suggested-assets', orgId, summaryId, allowedKey],
    queryFn: async ({ signal }) => {
      const res = await knowledgeGapsService.getSuggestedAssets(
        orgId,
        summaryId,
        { limit: SUGGESTED_LIMIT },
        { signal },
      );
      if (!res.success || !res.data) {
        throw new KGRequestError(
          res.error || 'Failed to load suggested assets.',
          res.code as KGAssetErrorCode | undefined,
        );
      }
      return res.data.assets.filter((a) =>
        allowedTypes.includes(a.asset_ref.asset_type),
      );
    },
  });

  // Memo so a missing list result doesn't churn a fresh `[]` identity each
  // render — that would invalidate every downstream useMemo unnecessarily.
  const assets = useMemo<KGRankedAsset[]>(
    () => listQuery.data ?? [],
    [listQuery.data],
  );

  // Batch key includes the sorted asset key list so a different ranked-list
  // composition (rare in practice — list query is also keyed) produces a
  // different cache entry rather than mixing verdicts across runs.
  const assetKeyList = useMemo(
    () => assets.map(assetKey).sort().join(','),
    [assets],
  );

  const batchQuery = useQuery({
    queryKey: [
      'kg-validate-batch',
      orgId,
      summaryId,
      allowedKey,
      assetKeyList,
    ] as const,
    queryFn: async ({ signal }) => {
      const res = await knowledgeGapsService.validateAssetsBatch(
        orgId,
        summaryId,
        { asset_refs: assets.map((a) => a.asset_ref) },
        { signal },
      );
      if (!res.success || !res.data) {
        throw new KGRequestError(
          res.error || 'Failed to validate sources.',
          res.code as KGAssetErrorCode | undefined,
        );
      }
      return res.data.results;
    },
    enabled: assets.length > 0,
  });

  const resultByAssetKey = useMemo(() => {
    const map = new Map<string, KGValidateAssetsBatchResultRow>();
    (batchQuery.data ?? []).forEach((row) => {
      map.set(`${row.asset_ref.asset_type}:${row.asset_ref.asset_id}`, row);
    });
    return map;
  }, [batchQuery.data]);

  const visibleRows: ValidatedRow[] = useMemo(() => {
    if (!batchQuery.data) return [];
    const rows: ValidatedRow[] = [];
    assets.forEach((asset) => {
      const entry = resultByAssetKey.get(assetKey(asset));
      if (entry?.result) {
        rows.push({ asset, validation: entry.result });
      }
    });
    return rows.slice().sort(compareRows);
  }, [assets, resultByAssetKey, batchQuery.data]);

  const failedRows: FailedRow[] = useMemo(() => {
    if (!batchQuery.data) return [];
    const rows: FailedRow[] = [];
    assets.forEach((asset) => {
      const entry = resultByAssetKey.get(assetKey(asset));
      if (entry?.error) {
        rows.push({ asset, error: entry.error });
      }
    });
    return rows;
  }, [assets, resultByAssetKey, batchQuery.data]);

  const recommendedRows = useMemo(
    () => visibleRows.filter((row) => !isNotRelevant(row)),
    [visibleRows],
  );

  const lessLikelyRows = useMemo(
    () => visibleRows.filter(isNotRelevant),
    [visibleRows],
  );

  // Auto-open the "Less likely" expander only when there are no recommended
  // rows to show — otherwise the user lands on what looks like an empty list.
  const lessLikelyDefaultOpen =
    recommendedRows.length === 0 && lessLikelyRows.length > 0;

  // Lift agent_not_configured anywhere in the result set (top-level OR per-row)
  // to a hard error block. Top-level surfacing matches the single-asset code
  // path, where agent_not_configured comes back as a service error.
  const agentNotConfigured =
    (listQuery.error instanceof KGRequestError &&
      listQuery.error.code === 'agent_not_configured') ||
    (batchQuery.error instanceof KGRequestError &&
      batchQuery.error.code === 'agent_not_configured') ||
    failedRows.some((row) => row.error.code === 'agent_not_configured');

  const hadCandidates = (listQuery.data?.length ?? 0) > 0;

  // Refetch only the batch when per-row failures are recoverable (Anthropic
  // hiccup, transient 5xx); cached rows return ~50ms from the backend DB
  // cache so this is effectively a per-row retry at the batch boundary.
  const retry = () => {
    if (listQuery.isError) {
      void listQuery.refetch();
      return;
    }
    void batchQuery.refetch();
  };

  // ── Render ───────────────────────────────────────────────────────────

  if (listQuery.isPending) {
    return <AssetListSkeleton count={3} />;
  }

  if (listQuery.isError || agentNotConfigured) {
    const isAgent = agentNotConfigured;
    const message = isAgent
      ? "This organization doesn't have a configured sales agent yet."
      : GENERIC_SOURCE_LOAD_ERROR;
    return (
      <div className="rounded-md border border-border p-4 text-sm space-y-3">
        <p className="font-medium text-foreground">
          {isAgent
            ? 'Sales agent is not configured'
            : 'Failed to load existing sources'}
        </p>
        <p className="text-muted-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={retry}>
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Retry
        </Button>
      </div>
    );
  }

  if (!hadCandidates) {
    return (
      <p className="text-sm text-muted-foreground">
        No ready assets available for this gap. Upload one on the Upload New tab.
      </p>
    );
  }

  if (batchQuery.isPending || (batchQuery.isFetching && !batchQuery.data)) {
    return <AssetListSkeleton count={Math.min(assets.length, 3)} />;
  }

  if (batchQuery.isError) {
    return (
      <div className="rounded-md border border-border p-4 text-sm space-y-3">
        <p className="font-medium text-foreground">
          Failed to validate existing sources
        </p>
        <p className="text-muted-foreground">{GENERIC_SOURCE_VALIDATE_ERROR}</p>
        <Button variant="outline" size="sm" onClick={retry}>
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Retry
        </Button>
      </div>
    );
  }

  if (
    visibleRows.length === 0 &&
    failedRows.length > 0 &&
    failedRows.length === assets.length
  ) {
    // Every row errored — give the user one clear retry path.
    return (
      <div className="rounded-md border border-border p-4 text-sm space-y-3">
        <p className="font-medium text-foreground">
          We couldn't validate any of your existing sources right now.
        </p>
        <p className="text-muted-foreground">
          Try again, or upload a new source on the Upload New tab.
        </p>
        <Button variant="outline" size="sm" onClick={retry}>
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-96 overflow-auto pr-1">
      {recommendedRows.length === 0 && lessLikelyRows.length > 0 && (
        <p className="text-sm text-muted-foreground">
          No strong matches found — review the less-likely options below.
        </p>
      )}

      {recommendedRows.map(({ asset, validation }) => {
        const key = assetKey(asset);
        return (
          <ExistingAssetRow
            key={key}
            asset={asset}
            validation={validation}
            onApply={onApply}
            isApplying={isApplying && selectedAssetKey === key}
            isAnyApplying={isApplying}
          />
        );
      })}

      {failedRows.length > 0 && failedRows.length < assets.length && (
        <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground flex items-center justify-between gap-3">
          <span>
            {failedRows.length === 1
              ? "Couldn't validate 1 source. Retry or skip."
              : `Couldn't validate ${failedRows.length} sources. Retry or skip.`}
          </span>
          <Button variant="ghost" size="sm" onClick={retry}>
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            Retry
          </Button>
        </div>
      )}

      {lessLikelyRows.length > 0 && (
        <Collapsible
          defaultOpen={lessLikelyDefaultOpen}
          className="pt-1"
        >
          <CollapsibleTrigger className="group flex w-full items-center justify-between rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground">
            <span>Less likely matches ({lessLikelyRows.length})</span>
            <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2 space-y-2">
            {lessLikelyRows.map(({ asset, validation }) => {
              const key = assetKey(asset);
              return (
                <ExistingAssetRow
                  key={key}
                  asset={asset}
                  validation={validation}
                  onApply={onApply}
                  isApplying={isApplying && selectedAssetKey === key}
                  isAnyApplying={isApplying}
                />
              );
            })}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

function ExistingAssetRow({
  asset,
  validation,
  onApply,
  isApplying,
  isAnyApplying,
}: {
  asset: KGRankedAsset;
  validation: KGAssetValidationResponse;
  onApply: (asset: KGRankedAsset) => void;
  isApplying: boolean;
  isAnyApplying: boolean;
}) {
  const Icon = ASSET_ICON[asset.asset_ref.asset_type];
  const typeLabel = ASSET_TYPE_LABEL[asset.asset_ref.asset_type];
  const hasDetails =
    validation.status === 'completed' &&
    (Boolean(validation.rationale) || validation.snippets.length > 0);

  return (
    <div className="rounded-md border border-border p-3 space-y-3">
      <div className="flex items-start gap-3">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{asset.title}</span>
            <Badge variant="secondary" className="text-[10px]">
              {typeLabel}
            </Badge>
          </div>
          <ValidationBadge result={validation} />
          {asset.summary && (
            <p className="text-xs text-muted-foreground line-clamp-2">
              {asset.summary}
            </p>
          )}
        </div>
        <Button
          size="sm"
          className="shrink-0"
          disabled={isAnyApplying}
          onClick={() => onApply(asset)}
        >
          {isApplying && (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          )}
          {isApplying ? 'Applying...' : 'Apply changes'}
        </Button>
      </div>

      {hasDetails && (
        <Collapsible>
          <CollapsibleTrigger className="group flex w-full items-center justify-between rounded-sm py-1 text-xs font-medium text-muted-foreground hover:text-foreground">
            <span>Why this might help</span>
            <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2 space-y-3">
            {validation.rationale && (
              <p className="text-sm text-muted-foreground leading-relaxed">
                {validation.rationale}
              </p>
            )}
            {validation.snippets.length > 0 && (
              <ul className="space-y-2">
                {validation.snippets.map((snippet, i) => (
                  <li
                    key={i}
                    className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-1"
                  >
                    {snippet.source_location && (
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        {snippet.source_location}
                      </div>
                    )}
                    <p className="leading-relaxed">{snippet.text}</p>
                  </li>
                ))}
              </ul>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

function ValidationBadge({ result }: { result: KGAssetValidationResponse }) {
  if (result.status === 'unavailable') {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          variant="outline"
          className="gap-1.5 border-amber-500/40 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300"
        >
          <MinusCircle className="h-3.5 w-3.5" />
          Validation unavailable
        </Badge>
      </div>
    );
  }

  if (!result.verdict) {
    return null;
  }

  const meta = VERDICT_META[result.verdict];
  const confidencePct =
    result.confidence !== null ? `${Math.round(result.confidence * 100)}%` : null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Badge variant="outline" className={cn('gap-1.5', meta.className)}>
        <meta.Icon className="h-3.5 w-3.5" />
        {meta.label}
      </Badge>
      {confidencePct && (
        <span className="text-xs text-muted-foreground">
          {result.verdict === 'not_relevant'
            ? `${confidencePct} sure this doesn't help`
            : `Confidence: ${confidencePct}`}
        </span>
      )}
    </div>
  );
}

function AssetListSkeleton({ count }: { count: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-24 w-full rounded-md" />
      ))}
    </div>
  );
}

function assetKey(asset: KGRankedAsset): string {
  return `${asset.asset_ref.asset_type}:${asset.asset_ref.asset_id}`;
}

function isNotRelevant(row: ValidatedRow): boolean {
  return (
    row.validation.status === 'completed' &&
    row.validation.verdict === 'not_relevant'
  );
}

// Tiers: lower number = higher priority in the rendered list.
//   0 — addresses
//   1 — partial
//   2 — unavailable (no verdict, but advisory rule keeps it visible)
//   3 — not_relevant (collapsed under "Less likely matches")
function verdictTier(result: KGAssetValidationResponse): number {
  if (result.status === 'unavailable') return 2;
  switch (result.verdict) {
    case 'addresses':
      return 0;
    case 'partial':
      return 1;
    case 'not_relevant':
      return 3;
    default:
      return 2;
  }
}

function compareRows(a: ValidatedRow, b: ValidatedRow): number {
  const tierA = verdictTier(a.validation);
  const tierB = verdictTier(b.validation);
  if (tierA !== tierB) return tierA - tierB;
  // Within a tier: highest confidence first (null treated as 0), then ranker score.
  const confA = a.validation.confidence ?? 0;
  const confB = b.validation.confidence ?? 0;
  if (confA !== confB) return confB - confA;
  return b.asset.relevance_score - a.asset.relevance_score;
}
